import { useRolodexFileLS, useRolodexSignedURLs } from 'features/rolodex'
import { useEffect, useMemo, useState } from 'react'
import { KVCameraPose, RolodexFileRef } from 'types'

export const useCameraPoses = (entryId: string | undefined) => {
  const [cameraPoses, setCameraPoses] = useState<KVCameraPose[]>([])
  const [isParsingLoading, setIsParsingLoading] = useState(false)
  const [parseError, setParseError] = useState<string | null>(null)

  // Get CameraPoses.csv file from rolodex
  const { fileRefs, isLoading, error } = useRolodexFileLS(
    entryId
      ? {
          entry_id: entryId,
          path: '/CameraPoses.csv', // Search for CameraPoses.csv
        }
      : undefined
  )

  // Filter for CameraPoses.csv file only
  const cameraPoseFile = useMemo(() => {
    const filtered = fileRefs.filter((file: RolodexFileRef) =>
      file.path.endsWith('CameraPoses.csv')
    )
    return filtered[0] // Return only the first (and should be only) match
  }, [fileRefs])

  // Get signed URL for the CameraPoses.csv file
  const { results: signedUrlResults, isLoading: signedUrlLoading } =
    useRolodexSignedURLs(
      cameraPoseFile
        ? [
            {
              entry_id: entryId || '',
              path: cameraPoseFile.path,
            },
          ]
        : []
    )

  // Get the signed URL
  const signedUrl = useMemo(() => {
    if (!cameraPoseFile) return null
    return signedUrlResults.find(
      (result) => result.path === cameraPoseFile.path
    )?.signed_url
  }, [cameraPoseFile, signedUrlResults])

  // Parse CSV content when signed URL is available
  useEffect(() => {
    if (!signedUrl) return

    const parseCameraPoses = async () => {
      setIsParsingLoading(true)
      setParseError(null)

      try {
        const response = await fetch(signedUrl)
        if (!response.ok) {
          throw new Error(`Failed to fetch CSV: ${response.statusText}`)
        }

        const csvText = await response.text()
        const lines = csvText.trim().split('\n')

        // Skip header row if it exists
        const dataLines = lines.slice(1)

        const poses: KVCameraPose[] = dataLines.map((line) => {
          const values = line.split(',').map((val) => val.trim())

          if (values.length < 7) {
            throw new Error(`Invalid CSV row: ${line}`)
          }

          return new KVCameraPose(
            values[0], // imageId
            parseFloat(values[1]), // x
            parseFloat(values[2]), // y
            parseFloat(values[3]), // z
            parseFloat(values[4]), // heading
            parseFloat(values[5]), // pitch
            parseFloat(values[6]) // roll
          )
        })

        setCameraPoses(poses)
      } catch (err) {
        setParseError(
          err instanceof Error ? err.message : 'Failed to parse CSV'
        )
        setCameraPoses([])
      } finally {
        setIsParsingLoading(false)
      }
    }

    parseCameraPoses()
  }, [signedUrl])

  return {
    cameraPoses,
    isLoading: isLoading || signedUrlLoading || isParsingLoading,
    error: error || parseError,
  }
}
